import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { useTheme2027 } from "../styles/theme2027";
import "./navTabs.css";

// The page reserves `topGap` + the track for the pinned strip. A 2027 page passes its own card gap, which its spacer
// repeats below the strip (Phil 2026-10-21); pages still under the old banner keep 8.
export const NAV_TABS_GAP = 8;
export const NAV_TABS_TRACK = 40;

const INSET = 3;

// SCO-1870: under native chrome (headerHeight param → --sh-chrome-top, set at boot in index.jsx) the strip parks below
// the header and dodges on scroll: hidden scrolling down, back on any scroll up, always shown near the top. Mirrors the
// native header fade rules (12px rest / 3px jitter).
export function useNavDodge() {
  const [dodged, setDodged] = useState(false);
  useEffect(() => {
    if (!document.documentElement.style.getPropertyValue("--sh-chrome-top")) return undefined;
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY;
      lastY = y;
      if (y <= 12) setDodged(false);
      else if (Math.abs(dy) >= 3) setDodged(dy > 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return dodged;
}

// Page tab strip (Design Guide 2027): floating segmented control, icon + label.
// items: [{ path, name, Icon }]; `disabled` keeps the tabs visible but inert. With `onChange` the tabs are buttons
// for in-page state instead of routes: items [{ key, name, Icon }], `value` = the active key (FAT's Find / My tournaments).
// `gutter` lines the strip up with a page whose cards sit off the 16px default (FAT: 10).
// `stacked`: the strip sits in the flow under the logo row and sticks once it reaches the top, `topGap` above it there
// (Phil 2026-11-02: the tabs go below the logo row); `bottomGap` is the room under it. The page reserves nothing.
// Scrolling down from the top, it scrolls off with the page; the dodge only acts once it is pinned.
// `children` ride along beside the track (FAT's web language chip).
export default function NavTabs({ items, disabled, dodged, label, value, onChange, className, gutter, topGap = NAV_TABS_GAP, stacked, bottomGap = 0, children }) {
  const t = useTheme2027();
  const { pathname } = useLocation();
  const active = onChange
    ? items.findIndex((item) => item.key === value)
    : items.findIndex((item) => matchPath({ path: `/${item.path}`, end: true }, pathname));
  // Equal tabs, but a long translated label can take more room, so the thumb is measured.
  const trackRef = useRef(null);
  const [thumb, setThumb] = useState(null);
  const names = items.map((item) => item.name).join("|");
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const measure = () => {
      const tab = track.querySelectorAll(".sh27-nav-tab")[active];
      setThumb(tab ? { left: tab.offsetLeft, width: tab.offsetWidth } : null);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    track.querySelectorAll(".sh27-nav-tab").forEach((tab) => observer.observe(tab));
    return () => observer.disconnect();
  }, [active, names]);
  // No slide on first paint: the transition switches on once a thumb has been placed.
  const placed = useRef(false);
  useLayoutEffect(() => {
    if (thumb) placed.current = true;
  }, [thumb]);

  // Stacked: a scroll down that starts with the strip in its own place lets it scroll off with the page ("loose"); once
  // it is pinned (the top section out of view) it dodges as before, and a scroll up slides it back in pinned (Phil
  // 2026-11-02). The sentinel marks the strip's own place, which sticky never moves.
  const sentinelRef = useRef(null);
  const navRef = useRef(null);
  const [mode, setMode] = useState("rest");
  const [slideIn, setSlideIn] = useState(false);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  useLayoutEffect(() => {
    if (!stacked || !sentinelRef.current) return;
    const chrome = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--sh-chrome-top")) || 0;
    const own = sentinelRef.current.getBoundingClientRect().top;
    if (dodged) {
      setMode(own >= chrome + topGap ? "loose" : "hidden");
      setSlideIn(false);
    } else {
      setSlideIn(modeRef.current === "loose" && own + (navRef.current?.offsetHeight || 0) <= chrome);
      setMode("rest");
    }
  }, [dodged, stacked, topGap]);
  const hidden = stacked ? mode === "hidden" : dodged;

  const nav = (
    <nav
      ref={navRef}
      className={[
        stacked && "sh27-nav-stack",
        stacked && mode === "loose" && "sh27-nav-loose",
        stacked && slideIn && "sh27-nav-in",
        hidden && "navDodge",
        className,
      ].filter(Boolean).join(" ")}
      style={stacked ? { top: `calc(var(--sh-chrome-top, 0px) + ${topGap}px)`, marginBottom: bottomGap, "--sh27-nav-pin": `${topGap}px` } : undefined}
      aria-label={label}
    >
      <div
        style={{
          position: "relative",
          maxWidth: 600,
          margin: "0 auto",
          padding: `${stacked ? 0 : topGap}px ${gutter ?? t.gutter}px 0`,
          boxSizing: "border-box",
        }}
      >
        <div
          ref={trackRef}
          className="sh27-nav"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "stretch",
            minHeight: NAV_TABS_TRACK,
            padding: INSET,
            boxSizing: "border-box",
            borderRadius: t.radius.button,
            background: t.navTrack,
            border: `1px solid ${t.line}`,
            boxShadow: t.navShadow,
            fontFamily: t.font,
          }}
        >
          {thumb && (
            <span
              aria-hidden="true"
              className={placed.current ? "sh27-nav-thumb sh27-nav-slide" : "sh27-nav-thumb"}
              style={{
                position: "absolute",
                top: INSET,
                bottom: INSET,
                left: 0,
                width: thumb.width,
                transform: `translateX(${thumb.left}px)`,
                borderRadius: t.radius.button - INSET,
                background: t.segOn,
                boxShadow: t.segShadow,
              }}
            />
          )}
          {items.map(({ path, key, name, Icon }, index) => {
            const on = index === active;
            const Tab = onChange ? "button" : Link;
            const tabProps = onChange
              ? { type: "button", onClick: () => !disabled && onChange(key), "aria-pressed": on }
              : { to: disabled ? "#" : path, "aria-current": on ? "page" : undefined };
            return (
              <Tab
                key={(onChange ? key : path) + "-" + index}
                {...tabProps}
                className="sh27-nav-tab"
                style={{
                  position: "relative",
                  flex: "1 1 0",
                  minWidth: "min-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  textDecoration: "none",
                  lineHeight: 1.1,
                  fontWeight: on ? 700 : 600,
                  color: on ? t.text : t.muted,
                  ...(onChange && { border: 0, margin: 0, background: "none", fontFamily: "inherit", cursor: "pointer" }),
                }}
              >
                {Icon && (
                  <span
                    aria-hidden="true"
                    className={on ? "sh27-nav-icon sh27-nav-icon-on" : "sh27-nav-icon"}
                  >
                    <Icon size="100%" color={on ? t.red : t.muted} />
                  </span>
                )}
                <span style={{ overflowWrap: "break-word" }}>{name}</span>
              </Tab>
            );
          })}
        </div>
        {children}
      </div>
    </nav>
  );
  if (!stacked) return nav;
  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" style={{ height: 0, width: "100%", flexShrink: 0 }} />
      {nav}
    </>
  );
}
